import { load, save } from '@/lib/storage'
import type { StoreDefinition } from '@/lib/storage'

/**
 * The two things a greeting needs to know from the last conversation: when it
 * happened, and what it was about.
 *
 * Deliberately not a zustand store, unlike everything else the app persists.
 * The reason is `forgetEverything`: a store keeps its own copy of the state in
 * memory and re-serialises all of it on the next write, so clearing storage is
 * not enough on its own and every store has to be reset by hand from the
 * profile store, which is what the long comment there is about. Reading and
 * writing straight through to storage means "delete everything" already
 * deletes this, and there is no fourth store somebody has to remember to add
 * to that list. Nothing renders these values and they are read once per
 * session, so there is no reactivity being given up.
 */

const VERSION = 1

export interface GreetingMemory {
  /** When Namu last heard from this person. Null on a first-ever visit. */
  lastSpokeAt: number | null
  /** A few words for what was last asked about. Only ever written with consent. */
  topic: string | null
}

/**
 * A topic longer than this is not a topic, it is a sentence somebody said.
 *
 * The extractor already refuses anything long, so this is the second gate
 * rather than the first: it bounds what a future caller, or a model reply that
 * turns out to be chatty, can put on the disk and then read back out loud.
 */
const TOPIC_MAX_CHARS = 60

function emptyMemory(): GreetingMemory {
  return { lastSpokeAt: null, topic: null }
}

const DEFINITION: StoreDefinition<GreetingMemory> = {
  key: 'greeting',
  version: VERSION,
  fallback: emptyMemory,
  validate: (data): data is GreetingMemory => {
    if (typeof data !== 'object' || data === null) return false
    const value = data as Partial<GreetingMemory>
    return (
      (value.lastSpokeAt === null || typeof value.lastSpokeAt === 'number') &&
      (value.topic === null || typeof value.topic === 'string')
    )
  },
}

export function readGreetingMemory(): GreetingMemory {
  return load(DEFINITION)
}

/**
 * Records that a conversation has just begun, and drops the previous topic.
 *
 * Clearing the topic here rather than at the end of the session is what stops
 * the continuity line from lying. If a session recorded nothing worth keeping,
 * leaving the previous topic in place means the visit after that one opens
 * with "last time we spoke about the clinic hours" when last time was about
 * something else entirely, or about nothing Namu understood. The line claims
 * to remember the previous conversation, so it has to be written by the
 * previous conversation.
 *
 * Callers must read the greeting before calling this. Marking first computes
 * the elapsed band against the session now starting, which puts everybody in
 * the shortest band forever.
 */
export function markSessionStart(at: number = Date.now()): void {
  save(DEFINITION, { ...readGreetingMemory(), lastSpokeAt: at, topic: null })
}

/**
 * Records that a conversation has ended.
 *
 * Separate from the start so that a long conversation is measured from when it
 * finished. Stamping only at the start meant somebody who talked for half an
 * hour and came back five minutes later was told "hello again" rather than
 * "yes?", because the elapsed time was counted from a point they were still
 * sitting there for.
 */
export function markSessionEnd(at: number = Date.now()): void {
  save(DEFINITION, { ...readGreetingMemory(), lastSpokeAt: at })
}

/**
 * Keeps what this conversation was about, if the person allowed it.
 *
 * This is the one value here that consent covers, and the line between the two
 * fields is worth stating. `lastSpokeAt` is a clock reading: it contains
 * nothing that was said, and the profile store already keeps `completedAt`
 * without consent on exactly that reasoning. Refusing to store it would
 * protect nobody and would cost somebody who declined the entire feature,
 * since a greeting that cannot tell morning from a month away is the recording
 * this was built to replace. The topic is different in kind. It is the
 * person's own words, held on the device between conversations, which is
 * precisely what the consent question asks about.
 *
 * A null topic leaves whatever this session has already recorded alone, so a
 * closing "thank you" that carries no topic does not wipe the real question
 * that came before it.
 *
 * Withdrawn consent clears rather than merely declining to write. Somebody who
 * turns it off has asked for what was kept to stop existing, not for the
 * collection to stop growing.
 */
export function rememberTopic(topic: string | null, consented: boolean): void {
  if (!consented) {
    const stored = readGreetingMemory()
    if (stored.topic !== null) save(DEFINITION, { ...stored, topic: null })
    return
  }
  if (topic === null) return
  const trimmed = topic.trim()
  if (trimmed.length === 0 || trimmed.length > TOPIC_MAX_CHARS) return
  save(DEFINITION, { ...readGreetingMemory(), topic: trimmed })
}
